// ./src/components/features/PlayListsTable/PlayListsTable.tsx
import React, { useMemo } from "react";
import { Text } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";

import { PlayList } from "@/utils/api/PlayListService";
import { BlockTransitionAnimationBadge } from "../../BlockTransitionAnimationBadge";
import { formatDateTime } from "@/utils";

export interface PlayListsTableProps {
  /** Array of playlists to display */
  playLists: PlayList[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditPlayList?: (playList: PlayList) => void;

  /** Callback when duplicate action is clicked */
  onDuplicatePlayList?: (playList: PlayList) => void;

  /** Callback when remove action is clicked */
  onRemovePlayList?: (playList: PlayList) => void;

  /** Callback when go into/view action is clicked */
  onGoIntoPlayList?: (playList: PlayList) => void;

  /** ID of playlist currently being removed */
  removingPlayListId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const PlayListsTable: React.FC<PlayListsTableProps> = ({
  playLists,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditPlayList,
  onDuplicatePlayList,
  onRemovePlayList,
  onGoIntoPlayList,
  removingPlayListId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<PlayList>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name & Description",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Untitled Playlist"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
            {record.playListItems && record.playListItems.length > 0 && (
              <Text size="xs" c="dimmed" mt={4}>
                {record.playListItems.length} item
                {record.playListItems.length !== 1 ? "s" : ""}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "blockTransitionAnimation",
        label: "Transition",
        sortable: false,
        width: 140,
        minWidth: 140,
        align: "left",
        render: (value) => {
          return <BlockTransitionAnimationBadge transitionType={value} />;
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 140,
        minWidth: 140,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "updatedAt",
        label: "Updated",
        sortable: true,
        width: 120,
        minWidth: 120,
        render: (_, record) => (
          <Text size="sm" c="dimmed">
            {formatDateTime(record.updatedAt || record.createdAt, "datetime")}
          </Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 230,
        minWidth: 230,
        align: "left",
        render: (_, record: any) => (
          <RecordActions
            context={record}
            onGoInClick={onGoIntoPlayList}
            onEditClick={
              record.rights?.includes("edit") ? onEditPlayList : undefined
            }
            onRemoveClick={
              record.rights?.includes("remove") ? onRemovePlayList : undefined
            }
            onDuplicateClick={
              record.rights?.includes("duplicate")
                ? onDuplicatePlayList
                : undefined
            }
            removeInProgress={removingPlayListId === record.id}
            goInTooltip="Edit play list items - content blocks"
          />
        ),
      },
    ],
    [
      onEditPlayList,
      onDuplicatePlayList,
      onRemovePlayList,
      onGoIntoPlayList,
      removingPlayListId,
    ]
  );

  return (
    <AppTable
      data={playLists}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(playList) => playList?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
